// Shared "producer's own location" blue dot for the producer-facing maps.
// Both the read-only Live Tracking view and the zone-editing setup map
// show the producer where they are physically standing, so the marker
// element and the create-or-move logic live here as the single source of
// truth. Kept framework-free (plain DOM + a Mapbox marker ref) so it can
// be unit tested without React.

import mapboxgl from "mapbox-gl";

/** [longitude, latitude] — the order Mapbox GL expects. */
export type LngLat = [number, number];

/**
 * Build the styled blue dot element for the producer's own location.
 * Matches the existing Live Tracking dot (pulsing blue, white ring) so
 * the two maps look identical. The `producer-pulse` keyframes live in
 * App.css.
 */
export const createProducerMarkerElement = (): HTMLDivElement => {
  const el = document.createElement("div");
  el.style.width = "20px";
  el.style.height = "20px";
  el.style.borderRadius = "50%";
  el.style.backgroundColor = "#3b82f6";
  el.style.border = "3px solid white";
  el.style.boxShadow = "0 0 0 4px rgba(59,130,246,0.3), 0 2px 4px rgba(0,0,0,0.3)";
  el.style.animation = "producer-pulse 2s ease-in-out infinite";
  el.title = "Your location";
  return el;
};

/**
 * Create the producer dot on first call, or just move it on later calls.
 * Returns the marker so the caller can store it in its ref. Mirrors the
 * create-once / setLngLat-thereafter pattern used by the crew markers.
 */
export const upsertProducerMarker = (
  map: mapboxgl.Map,
  existing: mapboxgl.Marker | null,
  location: LngLat,
): mapboxgl.Marker => {
  if (existing) {
    existing.setLngLat(location);
    return existing;
  }
  return new mapboxgl.Marker(createProducerMarkerElement()).setLngLat(location).addTo(map);
};
